import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import TileGraph from '../../../../components/TileGraph';
import graphOptions from '../../../../constants/constants';
import { ErrorField } from '../../../../lib/HooksFormFields';
import generateAssociatedElementsData from '../../../../utils/generateAssociatedElementsData';
import generateEntitiesData from '../../../../utils/generateEntitiesData';
import { getAssociatedLabelsAction } from '../../../../actions/indicators';
import {
  HIERARCHICAL_DONUT, hierarchicalCalcs, isHierarchicalLabels,
} from '../../../../utils/hierarchicalData';

import styles from './Graph.module.scss';

const Graph = () => {
  const {
    watch, setValue, options, errors, setError, clearErrors,
  } = useOutletContext();
  const config = watch('config');
  const graphType = watch('graph');
  const selectedName = watch('config.params.name');
  const selectedValue = watch('config.params.value');
  const data = watch('data');
  const api = watch('api')?.value;
  const key = watch('dataset')?.value;
  // the hierarchical donut is only offered when the selected field points to a hierarchical list
  // (null while the list has not been checked yet)
  const [isHierarchicalList, setIsHierarchicalList] = useState(null);

  function createConfig(type) {
    setValue('graph', type);
    const obj = { ...config };
    setValue('data', null);
    setValue('config', obj);
  }

  async function handleChangeData() {
    const isAssociatedDb = config?.params?.name.parent;
    let generatedData = null;
    const associatedConfig = {
      ...config,
      associatedConfig: {
        api: watch('api')?.value,
        key: watch('dataset')?.value,
      },
    };
    if (!isAssociatedDb || config.isAssociatedDb) {
      generatedData = await generateEntitiesData(
        options,
        associatedConfig,
        graphType,
        data,
      );
    } else {
      generatedData = await generateAssociatedElementsData(
        options,
        associatedConfig,
        graphType,
        data,
      );
    }
    if (generatedData?.stacked) {
      setValue('config.legend.stacked', generatedData.stacked);
    }
    if (generatedData?.error?.message) {
      setError('error', { type: 'custom', message: generatedData.error?.message });
    } else {
      clearErrors('error');
    }
    if (generatedData?.data) {
      setValue('data', generatedData.data);
    }
  }

  async function checkHierarchicalList() {
    const associatedId = selectedName?.associatedId || selectedName?.associatedDomainId;
    // nothing can be told yet, the list can not be requested
    if (!selectedName?.value || !api || !key) return null;
    if (!associatedId) return false;
    const res = await getAssociatedLabelsAction({
      lang: localStorage.getItem('lang') || 'fr',
      associatedId,
      api,
      key,
    });
    return isHierarchicalLabels(res?.data);
  }

  function isGraphAvailable(type) {
    const isAvailable = !!config?.params?.value?.graphs?.find((v) => v === type);
    if (type === HIERARCHICAL_DONUT) {
      // a donut of a distribution, one level of the list at a time
      return isHierarchicalList === true
        && !!config?.params?.value?.graphs?.find((v) => v === 'pie')
        && hierarchicalCalcs.includes(config?.params?.value?.value);
    }
    return isAvailable;
  }

  useEffect(() => {
    let isCurrent = true;
    checkHierarchicalList()
      .then((isHierarchical) => {
        if (isCurrent) setIsHierarchicalList(isHierarchical);
      })
      // the list stays unknown, the donut is not offered but a saved one is kept
      .catch(() => {
        if (isCurrent) setIsHierarchicalList(null);
      });
    return () => { isCurrent = false; };
  }, [selectedName?.value, api, key]);

  useEffect(() => {
    // an already selected donut is dropped if the field is not hierarchical anymore
    if (isHierarchicalList === null) return;
    if (graphType === HIERARCHICAL_DONUT && !isGraphAvailable(HIERARCHICAL_DONUT)) {
      setValue('graph', null);
      setValue('data', null);
    }
  }, [isHierarchicalList, selectedValue?.value]);

  useEffect(() => {
    if (options?.data
        && graphType
        && selectedName?.value
        && selectedValue?.value
    ) {
      handleChangeData();
    }
  }, [
    options?.data,
    config,
    selectedName,
    config?.params?.relative,
    selectedValue,
    graphType,
  ]);

  return (
    <>
    <div className={styles.container}>
      {graphOptions.map((g) => <TileGraph
        key={g}
        type={g}
        handleClick={() => createConfig(g)}
        disabled={!isGraphAvailable(g)}
        selected={watch('graph') === g}
      />)}

    </div>
      {errors?.error?.message
        && <ErrorField message={errors?.error?.message} />
      }
    </>
  );
};

export default Graph;
